// SourceColumnSetManage.jsx

import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import AIPanel from "../../../components/AIChat/AIPanel";
import { DialogControl, IconControl, Loading } from "spark-core-dx/components";
import { useCoreContext, useCoreStateContext } from "spark-core-dx/contexts";
import { useGenericManager, useOperationDialog } from "spark-core-dx/hooks";
import { ItemServiceFactory } from "../../../services/ItemServiceFactory";
import { sortOperations } from "../../../utilities/operationSort";
import ColumnSetEditor from "../../../components/ColumnSetEditor";
import { AI_PANEL_GRID_REFRESH_EVENT } from "../../../components/AIChat/aiPanelCommand";
import { withSourceObjectProcessorOperationDefaults } from "../sourceObjectProcessorType";
import "./sourceColumnSetManage.scss";

const firstItem = (value) => {
  if (!value) return null;
  if (typeof value?.first === "function") return value.first();
  return Array.isArray(value) ? value[0] : null;
};

const getSourceObjectId = (value) =>
  value?.SourceObjectId || value?._id || value?.UniqueId || value?.Id || value?.id || null;

const getCurrentRelativeUrl = () => {
  if (typeof window === "undefined" || !window.location) return "/";
  return `${window.location.pathname}${window.location.search}${window.location.hash}` || "/";
};

const resolveBackUrl = (searchParams) => {
  const rawBackUrl =
    searchParams.get("backUrl") ||
    searchParams.get("backurl") ||
    searchParams.get("returnUrl") ||
    searchParams.get("returnurl") ||
    "";

  if (!rawBackUrl) return getCurrentRelativeUrl();

  let decodedBackUrl = rawBackUrl;
  try {
    decodedBackUrl = decodeURIComponent(rawBackUrl);
  } catch {
    decodedBackUrl = rawBackUrl;
  }

  if (decodedBackUrl.startsWith("/") && !decodedBackUrl.startsWith("//")) {
    return decodedBackUrl;
  }

  try {
    const url = new URL(decodedBackUrl, window.location.origin);
    if (url.origin === window.location.origin) {
      return `${url.pathname}${url.search}${url.hash}`;
    }
  } catch {
    // Fall through to current page fallback.
  }

  return getCurrentRelativeUrl();
};

const parseMaybeJson = (value) => {
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  if (!trimmed || !["{", "["].includes(trimmed[0])) return value;
  try {
    return JSON.parse(trimmed);
  } catch {
    return value;
  }
};

const normalizeParamsObject = (source) => {
  const parsed = parseMaybeJson(source);
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
  return parsed;
};

const getPanelParam = (params, ...keys) => {
  if (!params || typeof params !== "object") return "";
  for (const key of keys) {
    const value = params[key];
    if (value !== undefined && value !== null && String(value).trim()) {
      return String(value).trim();
    }
  }
  return "";
};

const SOURCE_OBJECT_ITEM_NAME = "SourceObjectItem";
const MANAGE_SOURCE_OBJECT_OPERATION = "ManageSourceObject";
const SEARCH_SOURCE_OBJECT_OPERATION = "SearchSourceObject";
const PANEL_TRANSITION_MS = 260;
const SOURCE_OBJECT_MANAGER_PANEL_WIDTH = 1225;
const EXCLUDED_SOURCE_OBJECT_OPERATION_IDS = new Set([
  "ManageSourceObject",
  "AIQuestionSourceObjectManage",
  "AIQuestionEDIDiagnostics",
]);
const EXCLUDED_SOURCE_OBJECT_OPERATION_LABELS = new Set([
  "Manage Source",
  "Manage Source Object",
  "Manage Source Configuration",
  "Analyze EDI Diagnostics",
]);

const getOperationName = (operation) => operation?.Name || operation?.OperationTypeId || operation?.Value || operation?._id || operation?.Id || "";

const getOperationDisplayName = (operation) => operation?.DisplayName || operation?.Text || getOperationName(operation);

const isSourceObjectOperation = (operation) =>
  operation?.SupportsOperationItem === true &&
  (operation?.ItemName === SOURCE_OBJECT_ITEM_NAME || operation?.MetadataRefItemName === SOURCE_OBJECT_ITEM_NAME);

const isExcludedSourceObjectOperation = (operation) =>
  EXCLUDED_SOURCE_OBJECT_OPERATION_IDS.has(getOperationName(operation)) ||
  EXCLUDED_SOURCE_OBJECT_OPERATION_LABELS.has(getOperationDisplayName(operation));

const normalizeOperationForDialog = (operation) => {
  const name = getOperationName(operation);
  return {
    ...operation,
    Name: name,
    OperationTypeId: operation?.OperationTypeId || name,
    DisplayName: getOperationDisplayName(operation),
  };
};

const SourceColumnSetManage = ({
  panelMode = false,
  panelOpen = true,
  panelId = "",
  panelSessionId = "",
  panelParams = null,
  onPanelClose = null,
  embeddedMode = false,
  editObjectRequestKey = 0,
  onEmbeddedClose = null
}) => {
  const itemService = useMemo(() => new ItemServiceFactory(), []);
  const itemServiceEnum = itemService?.ItemServiceEnum ?? {
    SourceObject: "SourceObject",
    AIChatSession: "AIChatSession",
  };
  const navigate = useNavigate();
  const coreState = useCoreStateContext();
  const coreContext = useCoreContext();
  const pageRef = useRef(null);

  const sourceObjectMgr = useGenericManager(itemServiceEnum.SourceObject);
  const chatSessionMgr = useGenericManager(itemServiceEnum.AIChatSession);
  const {
    handleOperationDialog,
    activeOperation,
    operationSuccessMessage,
    operationErrors,
    cancelOperation,
    renderOperationContent,
    executeOperation,
    openDialog,
  } = useOperationDialog(itemServiceEnum.SourceObject);
  const sourceObjectMgrRef = useRef(sourceObjectMgr);
  const chatSessionMgrRef = useRef(chatSessionMgr);
  const coreStateRef = useRef(coreState);
  sourceObjectMgrRef.current = sourceObjectMgr;
  chatSessionMgrRef.current = chatSessionMgr;
  coreStateRef.current = coreState;

  const [sourceObject, setSourceObject] = useState(null);
  const [currentSourceObject, setCurrentSourceObject] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [refresh, setRefresh] = useState("0");
  const [session, setSession] = useState(null);
  const [metadata, setMetadata] = useState(null);
  const [panelContentReady, setPanelContentReady] = useState(!panelMode);

  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const searchParamsKey = searchParams.toString();
  const backUrl = useMemo(() => resolveBackUrl(searchParams), [searchParamsKey, searchParams]);
  const resolvedPanelParams = useMemo(() => normalizeParamsObject(panelParams), [panelParams]);
  const sourceSystemId = getPanelParam(resolvedPanelParams, "sourceSystemId", "SourceSystemId");
  const effectiveId = useMemo(() => (
    String(panelId ?? "").trim() ||
    getPanelParam(resolvedPanelParams, "sourceObjectId", "SourceObjectId", "id", "_id") ||
    String(searchParams.get("sourceObjectId") ?? searchParams.get("id") ?? "").trim() ||
    String(id ?? "").trim()
  ), [id, panelId, resolvedPanelParams, searchParams]);
  const lastPayloadStrRef = useRef("");
  const loadKeyRef = useRef(null);
  const canLoadData = !panelMode || panelContentReady;

  const sourceObjectOperations = useMemo(() => {
    const operations = Array.isArray(coreContext?.Operations) ? coreContext.Operations : [];
    const visibleOperations = operations
      .filter(isSourceObjectOperation)
      .filter((operation) => !isExcludedSourceObjectOperation(operation))
      .filter((operation) => !panelMode || getOperationName(operation) !== SEARCH_SOURCE_OBJECT_OPERATION)
      .map(normalizeOperationForDialog);
    return sortOperations(visibleOperations);
  }, [coreContext?.Operations, panelMode]);
  const sourceObjectManagerTitle = useMemo(() => {
    const operations = Array.isArray(coreContext?.Operations) ? coreContext.Operations : [];
    const manageOperation = operations.find((operation) => getOperationName(operation) === MANAGE_SOURCE_OBJECT_OPERATION);
    return getOperationDisplayName(manageOperation) || "Manage Source";
  }, [coreContext?.Operations]);

  const dispatchGridRefresh = useCallback((reason = "source-object-save", sourceObjectId = effectiveId) => {
    if (typeof window === "undefined") return;
    window.dispatchEvent(new CustomEvent(AI_PANEL_GRID_REFRESH_EVENT, {
      cancelable: true,
      detail: {
        panel: "sourceobjectmanage",
        itemName: "SourceObject",
        id: sourceObjectId,
        sourceObjectId,
        sourceSystemId,
        reason
      }
    }));
  }, [effectiveId, sourceSystemId]);

  const setCurrentSourceObjectIfChanged = useCallback((next) => {
    let nextStr = "";
    try {
      nextStr = JSON.stringify(next);
    } catch {
      /* ignore */
    }

    if (nextStr && nextStr === lastPayloadStrRef.current) return;

    setCurrentSourceObject((prev) => {
      try {
        const prevStr = JSON.stringify(prev);
        if (prevStr === nextStr) return prev;
      } catch {
        if (prev === next) return prev;
      }
      lastPayloadStrRef.current = nextStr || "";
      return next;
    });
  }, []);

  useEffect(() => {
    if (!panelMode) {
      setPanelContentReady(true);
      return undefined;
    }

    if (!panelOpen) {
      setPanelContentReady(false);
      return undefined;
    }

    const timerId = window.setTimeout(() => {
      setPanelContentReady(true);
    }, PANEL_TRANSITION_MS);

    return () => window.clearTimeout(timerId);
  }, [panelMode, panelOpen]);

  useEffect(() => {
    if (!canLoadData) {
      return undefined;
    }

    if (!effectiveId) {
      setIsLoading(false);
      setSourceObject(null);
      setCurrentSourceObjectIfChanged(null);
      return undefined;
    }

    const loadKey = `${panelMode ? "panel" : "route"}|${effectiveId}|${panelSessionId || ""}`;
    if (loadKeyRef.current === loadKey) {
      return undefined;
    }

    loadKeyRef.current = loadKey;
    let cancelled = false;

    const getChatSession = async (referenceId) => {
      if (!referenceId) return null;
      if (panelSessionId) {
        return {
          _id: panelSessionId,
          ReferenceId: referenceId,
          ReferenceItemName: "SourceObjectManage"
        };
      }

      const chatSessionResult = await chatSessionMgrRef.current.Query({
        model:
          "x => x.ReferenceId == referenceId && x.ReferenceItemName == referenceItemName && x.IsDeleted == false && x.IsActive == true",
        params: { referenceId, referenceItemName: "SourceObjectManage" },
      });

      if (chatSessionResult?.Success && chatSessionResult?.Items?.length > 0) {
        return firstItem(chatSessionResult.Items);
      }
      return null;
    };

    const load = async () => {
      setIsLoading(true);

      try {
        let nextSourceObject = null;
        const redirectData = coreStateRef.current?.get?.("InternalRedirectData");
        if (!panelMode && redirectData?.SourceObjectId && (effectiveId === redirectData._id || effectiveId === redirectData.SourceObjectId)) {
          nextSourceObject = redirectData;
        } else {
          const result = await sourceObjectMgrRef.current.getItem(effectiveId);
          if (result?.Success && result.Items?.length > 0) {
            nextSourceObject = firstItem(result.Items);
          }
        }

        if (!cancelled && nextSourceObject) {
          setSourceObject(nextSourceObject);
          setCurrentSourceObjectIfChanged(nextSourceObject);
          const nextSession = await getChatSession(getSourceObjectId(nextSourceObject));
          if (!cancelled) setSession(nextSession);
        }

        const metadataResp = await sourceObjectMgrRef.current.GetMetadata({});
        if (!cancelled) setMetadata(metadataResp?.Items ?? []);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [canLoadData, effectiveId, panelMode, panelSessionId, setCurrentSourceObjectIfChanged]);

  const onUpdatePanel = async (UICommand, item, value) => {
    if (UICommand !== "update") return;

    const normalizeDraftPayload = (candidate) => {
      if (!candidate || typeof candidate !== "object") return null;
      if (Array.isArray(candidate.Fields)) return candidate;
      if (Array.isArray(candidate.fields)) return { ...candidate, Fields: candidate.fields };
      return null;
    };

    const updatedSourceObject =
      normalizeDraftPayload(item) ??
      normalizeDraftPayload(value) ??
      normalizeDraftPayload(value?.sourceObject) ??
      normalizeDraftPayload(value?.output) ??
      normalizeDraftPayload(value?.output?.sourceObject) ??
      null;

    if (updatedSourceObject?.Fields) {
      setCurrentSourceObjectIfChanged(updatedSourceObject);
      setRefresh({ ...updatedSourceObject, acceptedAt: Date.now() });
    }
  };

  const handleSaveSourceObject = async (updatedSourceObject) => {
    const result = await sourceObjectMgr.saveItem(updatedSourceObject);
    if (result?.Success) {
      const saved = firstItem(result.Items);
      setRefresh(String(Date.now()));
      setSourceObject(saved);
      setCurrentSourceObjectIfChanged(saved);
      dispatchGridRefresh("source-object-save", getSourceObjectId(saved) || effectiveId);
    }
    return result;
  };

  const handleCurrentSourceObject = (value) => {
    setCurrentSourceObjectIfChanged(value);
  };

  const handleSourceOperationClick = async (operation) => {
    if (!operation || !sourceObject) return;
    const activeSourceObject = currentSourceObject || sourceObject;

    await handleOperationDialog(
      {
        ...withSourceObjectProcessorOperationDefaults(operation, activeSourceObject),
        itemData: activeSourceObject,
      },
      { closeOnComplete: false }
    );
  };

  const handleBack = useCallback(() => {
    navigate(backUrl);
  }, [backUrl, navigate]);

  const handleCancel = useCallback(() => {
    if (panelMode) {
      dispatchGridRefresh("panel-close", getSourceObjectId(sourceObject) || effectiveId);
      if (typeof onPanelClose === "function") onPanelClose();
      return;
    }

    if (embeddedMode) {
      dispatchGridRefresh("embedded-close", getSourceObjectId(sourceObject) || effectiveId);
      if (typeof onEmbeddedClose === "function") onEmbeddedClose();
      return;
    }

    handleBack();
  }, [dispatchGridRefresh, effectiveId, embeddedMode, handleBack, onEmbeddedClose, onPanelClose, panelMode, sourceObject]);

  const renderPanelContent = () => {
    if (isLoading) {
      return (
        <div className="centered-loading" ref={pageRef}>
          <Loading />
        </div>
      );
    }

    return (
      <div className="column-set-editor" ref={pageRef}>
        <ColumnSetEditor
          sourceObject={sourceObject}
          fields={sourceObject?.Fields ?? []}
          fieldItemMetadata={metadata?.find((x) => x.Name === "SourceObjectField")}
          sourceObjectItemMetadata={metadata?.find((x) => x.Name === "SourceObject")}
          onSave={handleSaveSourceObject}
          onChange={handleCurrentSourceObject}
          refresh={refresh}
          sourceObjectOperations={sourceObjectOperations}
          onSourceObjectOperationClick={handleSourceOperationClick}
          sidePanelEditingEnabled={!panelMode}
          fieldEditingPresentation={panelMode ? "inline" : "sidePanel"}
          editObjectRequestKey={editObjectRequestKey}
          showSourceObjectEditAction={!embeddedMode}
          showSourceObjectOperations={!embeddedMode}
          responsiveAvailablePickerEnabled={true}
        />
      </div>
    );
  };

  const sourceObjectId = getSourceObjectId(sourceObject) || effectiveId;

  if (!panelMode && isLoading) {
    return (
      <div className="centered-loading">
        <Loading />
      </div>
    );
  }

  return (
    <div className="source-column-set-screen">
      {!panelMode && !embeddedMode ? (
        <div className="source-column-set-screen__header">
          <h2 className="source-column-set-screen__title">
            <IconControl iconKey={"schema"} /> Source Object Designer
          </h2>
          <div className="source-column-set-screen__header-actions">
            <button type="button" className="source-column-set-screen__back" onClick={handleBack}>
              {"<<"} Back
            </button>
          </div>
        </div>
      ) : null}
      <DialogControl
        controlOrigin="source-object-execute-operation"
        openDialog={openDialog}
        title={activeOperation?.DisplayName ?? activeOperation?.Name ?? "Operation Completed"}
        subTitle={activeOperation?.Details}
        onCancel={cancelOperation}
        onOk={async () => executeOperation()}
        excludeCancel={!activeOperation}
        okText={activeOperation && operationErrors ? "Retry" : "OK"}
        disableCancel={operationSuccessMessage}
      >
        {renderOperationContent()}
      </DialogControl>
      <AIPanel
        childrenRef={pageRef}
        open={panelMode ? panelOpen : true}
        presentation={panelMode ? "slideout" : "inline"}
        title={sourceObjectManagerTitle}
        className={panelMode ? "ai-panel-floating-slideout source-column-set-panel" : ""}
        onUpdatePanel={onUpdatePanel}
        onSessionChange={(sessionId) => {
          if (sessionId) {
            setSession({
              _id: sessionId,
              ReferenceId: sourceObjectId,
              ReferenceItemName: "SourceObjectManage"
            });
          }
        }}
        mapToClass="core-layout"
        width={panelMode ? SOURCE_OBJECT_MANAGER_PANEL_WIDTH : undefined}
        minPanelWidth={panelMode ? SOURCE_OBJECT_MANAGER_PANEL_WIDTH : undefined}
        panelStorageKey={panelMode ? null : undefined}
        contentReady={panelMode ? panelContentReady : true}
        params={{ id: sourceObjectId, sourceObjectId }}
        session={session}
        payload={currentSourceObject}
        referenceId={sourceObjectId}
        referenceName="SourceObjectManage"
        operationName="AIQuestionSourceObjectManage"
        defaultOrchestrationMode="AgentAssist"
        singleSessionMode={true}
        planningPreviewEnabled={false}
        enableContentDragHiding={false}
        minLeft={825}
        minRight={400}
        startChat={null}
        splashMessage="How can I help you manage this source object?"
        introMessage="Manage Source Configuration..."
        onCancel={handleCancel}
      >
        {renderPanelContent()}
      </AIPanel>
    </div>
  );
};

export default SourceColumnSetManage;
